/**
 * LevelUpAnimation Component
 *
 * Celebratory animation for level up events.
 * Shows animated level transition with confetti-like effects.
 */

import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { TrendingUp, Star, Sparkles, Award } from 'lucide-react'

const LevelUpAnimation = ({ fromLevel, toLevel, onComplete }) => {
  const [stage, setStage] = useState('initial') // 'initial' | 'transitioning' | 'complete'
  const [showConfetti, setShowConfetti] = useState(false)

  useEffect(() => {
    // Animation sequence
    const timer1 = setTimeout(() => {
      setStage('transitioning')
      setShowConfetti(true)
    }, 300)

    const timer2 = setTimeout(() => {
      setStage('complete')
    }, 1000)

    const timer3 = setTimeout(() => {
      setShowConfetti(false)
      if (onComplete) onComplete()
    }, 3000)

    return () => {
      clearTimeout(timer1)
      clearTimeout(timer2)
      clearTimeout(timer3)
    }
  }, [onComplete])

  const levelDiff = toLevel - fromLevel

  return (
    <div className="relative p-6 text-center overflow-hidden">
      {/* Confetti particles */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10%',
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${1 + Math.random()}s`
              }}
            >
              <Star
                className={`w-4 h-4 ${
                  ['text-yellow-400', 'text-purple-400', 'text-blue-400', 'text-pink-400'][
                    Math.floor(Math.random() * 4)
                  ]
                }`}
                fill="currentColor"
              />
            </div>
          ))}
        </div>
      )}

      {/* Main content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <Sparkles className="w-6 h-6 text-yellow-500 animate-pulse" />
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Level Up!</h3>
          <Sparkles className="w-6 h-6 text-yellow-500 animate-pulse" />
        </div>

        {/* Level transition */}
        <div className="flex items-center justify-center gap-4 my-6">
          {/* From level */}
          <div
            className={`transition-all duration-500 ${
              stage === 'initial'
                ? 'scale-100 opacity-100'
                : stage === 'transitioning'
                  ? 'scale-75 opacity-50'
                  : 'scale-50 opacity-0'
            }`}
          >
            <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <span className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                {fromLevel}
              </span>
            </div>
          </div>

          {/* Arrow */}
          <div
            className={`transition-all duration-500 ${
              stage === 'transitioning' || stage === 'complete'
                ? 'scale-100 opacity-100'
                : 'scale-0 opacity-0'
            }`}
          >
            <TrendingUp className="w-8 h-8 text-success-500 animate-bounce" />
          </div>

          {/* To level */}
          <div
            className={`transition-all duration-700 ${
              stage === 'complete'
                ? 'scale-110 opacity-100'
                : stage === 'transitioning'
                  ? 'scale-100 opacity-100'
                  : 'scale-75 opacity-0'
            }`}
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 flex items-center justify-center shadow-lg animate-glow">
              <span className="text-3xl font-bold text-white">{toLevel}</span>
            </div>
          </div>
        </div>

        {/* Message */}
        <div
          className={`transition-all duration-500 ${
            stage === 'complete' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="flex items-center justify-center gap-2 text-success-600 dark:text-success-400 mb-2">
            <Award className="w-5 h-5" />
            <span className="font-semibold">
              +{levelDiff} Level{levelDiff > 1 ? 's' : ''}
            </span>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            You're now at Level {toLevel}! Keep up the great work!
          </p>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(400px) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(251, 191, 36, 0.4);
          }
          50% {
            box-shadow: 0 0 40px rgba(251, 191, 36, 0.8);
          }
        }

        .animate-confetti {
          animation: confetti linear forwards;
        }

        .animate-glow {
          animation: glow 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}

LevelUpAnimation.propTypes = {
  fromLevel: PropTypes.number.isRequired,
  toLevel: PropTypes.number.isRequired,
  onComplete: PropTypes.func
}

LevelUpAnimation.defaultProps = {
  onComplete: null
}

export default LevelUpAnimation
